import React, { useContext } from 'react';
import {
  Button,
  Image,
  Link,
  Text,
  Title3,
} from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useAcceptInvitationStyles } from '../styles';
import FacebookIcon from '../../../media/btn-facebook.svg';
import HeaderLogo from '../../../media/microsoft-advertising-logo-idp-header.png';

// "Learn more" target for the sign-in explainer. Same Microsoft Advertising help host the pre-auth
// landing page uses for its sign-in help link.
const LEARN_MORE_URL = 'https://help.ads.microsoft.com/#apex/ads/en/60259/0';

export type ActivationFacebookAccountRequiredProps = {
  // The Facebook account email this invitation's user is registered with. Shown inline so the user
  // knows which account to sign in with. Optional — omitted from the copy when unavailable.
  email?: string | null;
  // "Sign in with Facebook" — sign the current MSA/AAD/Google session out and send the user to
  // Facebook sign-in.
  onSignInWithFacebook: () => void;
  // "Go back" — sign the current session out and return to the default invitation choice.
  onGoBack: () => void;
};

// Shown when ActivationOptions reports the invited email is registered with a Facebook account, but
// the user signed in with a different provider (personal Microsoft / work-school / Google). To accept
// the invitation they must re-authenticate with Facebook — both actions sign the current session out
// first (the URLs are resolved by the caller). Mirror of ActivationGoogleAccountRequired.
export const ActivationFacebookAccountRequired: React.FC<ActivationFacebookAccountRequiredProps> = ({
  email,
  onSignInWithFacebook,
  onGoBack,
}) => {
  const i18n = useContext(I18nContext) as any;
  const classes = useAcceptInvitationStyles();

  const accountLine = email
    ? i18n.getString(
      _TL_('Your Microsoft Advertising account for {{email}} uses Facebook sign-in.'),
      { email }
    )
    : i18n.getString(_TL_('Your Microsoft Advertising account uses Facebook sign-in.'));

  return (
    <div className={classes.centeredPage}>
      <div
        className={classes.errorCard}
        role="region"
        aria-labelledby="activation-facebook-required-title"
        aria-describedby="activation-facebook-required-description"
      >
        <Image className={classes.brandLogo} src={HeaderLogo} alt={i18n.getString(_TL_('Microsoft Advertising'))} />

        <Title3 id="activation-facebook-required-title" className={classes.errorTitle}>
          {i18n.getString(_TL_('Sign in with your Facebook account'))}
        </Title3>

        <div id="activation-facebook-required-description" className={classes.errorBody}>
          <Text block>{accountLine}</Text>
          <Text block>
            {i18n.getString(_TL_('To continue, sign in with your Facebook account.'))}
            {' '}
            <Link href={LEARN_MORE_URL} target="_blank" rel="noopener noreferrer">
              {i18n.getString(_TL_('Learn More'))}
            </Link>
          </Text>
        </div>

        <div className={classes.errorActions}>
          <Button
            className="facebook-account-continue"
            appearance="primary"
            icon={<Image src={FacebookIcon} width={16} height={16} alt="" aria-hidden="true" />}
            onClick={onSignInWithFacebook}
          >
            {i18n.getString(_TL_('Sign in with Facebook'))}
          </Button>
          <Button
            className="facebook-account-go-back"
            appearance="secondary"
            onClick={onGoBack}
          >
            {i18n.getString(_TL_('Go back'))}
          </Button>
        </div>
      </div>
    </div>
  );
};
